import { React, useState, useEffect, useCallback } from 'react'
import { Container, Nav, Navbar, Offcanvas, Row, Stack, Card, Col, Spinner, Table, Badge, Alert } from 'react-bootstrap';
import UserLocationMap from '../Components/UserLocationMap';
import Login from '../Components/Admin/Login';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Navigation from '../Components/Admin/Navigation';
import { functions } from '../utils/firebase';
import { httpsCallable } from 'firebase/functions';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { nl } from 'date-fns/locale';

// Register Dutch locale
registerLocale('nl', nl);

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Define brand colors
const brandColors = {
  primaryDark: '#3b4f46',    // var(--ukies-dark-green)
  primaryLight: '#567a70',   // var(--ukies-light-green)
  secondaryDark: '#d19112',  // var(--ukies-dark-orange)
  secondaryLight: '#ffb41e', // var(--ukies-light-orange)
  disabled: '#910714',       // var(--ukies-disabled)
  inactive: '#6c757d',       // var(--ukies-inactive)
};

export default function UkiesAdmin(props) {
    const auth = getAuth();
    const [ token, setToken ] = useState(localStorage.getItem('ukies-webinars-accessToken'))
    const [ user, setUser ] = useState(JSON.parse(localStorage.getItem('ukies-webinars-admin')))
    
    // Analytics state
    const [ analyticsData, setAnalyticsData ] = useState({
        timeSeries: [],
        visitorStats: {
            totalVisitors: 0,
            uniqueVisitors: 0,
            returningVisitors: 0,
            activeVisitors: 0
        },
        pageViews: {
            totalPageViews: 0,
            totalSessions: 0,
            homePageViews: 0,
            seriesPageViews: 0,
            videoPageViews: 0,
            filePageViews: 0
        },
        engagement: {
            avgEngagementTime: 0,
            avgSessionDuration: 0
        },
        topContent: {
            topSeries: [],
            topVideos: [],
            topFiles: []
        },
        realtimeData: {
            currentActiveUsers: 0,
            timeSeries: [],
            lastUpdated: null,
            dataSource: "Google Analytics 4 Real-time API",
        }
    })
    const [ analyticsLoading, setAnalyticsLoading ] = useState(true)
    const [ realtimeLoading, setRealtimeLoading ] = useState(false)
    const [ analyticsError, setAnalyticsError ] = useState(null)
    const [ lastUpdated, setLastUpdated ] = useState(null)
    const [ realtimeLastUpdated, setRealtimeLastUpdated ] = useState(null)
    const [ selectedMetric, setSelectedMetric ] = useState('visitorStats')
    const [ showAlert, setShowAlert ] = useState(false)
    const [ alertMessage, setAlertMessage ] = useState('')
    const [ alertType, setAlertType ] = useState('success')
    const [ showDatePicker, setShowDatePicker ] = useState(false)
    const [ selectedPreset, setSelectedPreset ] = useState('Laatste 30 dagen')
    const [ startDate, setStartDate ] = useState(moment().subtract(30, 'days').toDate())
    const [ endDate, setEndDate ] = useState(new Date())


    // Predefined date range options
    const getPresetDateRange = (preset) => {
        const today = new Date();
        let startDate;
        
        switch (preset) {
            case 'Vandaag':
                startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // Today only
                break;
            case 'Dit jaar':
                startDate = new Date(today.getFullYear(), 0, 1); // January 1st this year
                break;
            case 'Deze maand':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1); // First of current month
                break;
            case 'Deze week':
                const dayOfWeek = today.getDay();
                const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Make Monday first day of week
                startDate = new Date(today);
                startDate.setDate(today.getDate() + mondayOffset);
                break;
            case 'Laatste 30 dagen':
                startDate = moment().subtract(30, 'days').toDate();
                break;
            case 'Laatste 90 dagen':
                startDate = moment().subtract(90, 'days').toDate();
                break;
            default:
                startDate = moment().subtract(30, 'days').toDate();
        }
        
        return {
            startDate: startDate,
            endDate: today
        };
    };

    // Format date for Firebase function (YYYY-MM-DD)
    const formatDateForApi = (date) => {
        if (!date) {
            console.error('formatDateForApi received null/undefined date:', date);
            return null;
        }
        const formatted = moment(date).format('YYYY-MM-DD');
        console.log('formatDateForApi:', date, '->', formatted);
        return formatted;
    };

    // Chart configuration
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: selectedMetric === 'visitorStats',
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                }
            },
            title: {
                display: false,
            },
            tooltip: {
                mode: selectedMetric === 'activeUsers' ? 'nearest' : 'index',
                intersect: selectedMetric === 'activeUsers',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#000',
                bodyColor: '#000',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                callbacks: selectedMetric === 'activeUsers' ? {
                    label: function(context) {
                        // Show actual value in tooltip, not the modified value for tiny bars
                        const actualData = context.dataset.actualData;
                        const actualValue = actualData ? actualData[context.dataIndex] : context.parsed.y;
                        return `${context.dataset.label}: ${actualValue} gebruikers`;
                    }
                } : undefined,
            },
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: selectedMetric === 'activeUsers',
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#666',
                },
            },
            y: {
                display: true,
                grid: {
                    color: '#f0f0f0',
                },
                ticks: {
                    color: '#666',
                    stepSize: selectedMetric === 'activeUsers' ? 1 : undefined, // Integer steps for active users
                    callback: selectedMetric === 'activeUsers' ? function(value) {
                        // Only show whole numbers for active users, hide 0.1 (tiny bar indicator)
                        if (value === 0.1) return ''; // Hide the tiny bar value
                        return Number.isInteger(value) ? value : '';
                    } : undefined,
                },
                beginAtZero: true,
                min: 0,
                max: selectedMetric === 'activeUsers' ? function(context) {
                    // For active users, ensure minimum range of 0-3
                    const actualData = context.chart.data.datasets[0].actualData || context.chart.data.datasets[0].data;
                    const dataMax = Math.max(...actualData);
                    return Math.max(3, dataMax + 1);
                } : undefined,
            },
        },
        elements: {
            line: {
                tension: 0,
                borderWidth: 2,
            },
            point: {
                radius: 3,
                hoverRadius: 6,
            },
            bar: {
                borderWidth: 0,
            },
        },
    };

    // Handle preset selection
    const handlePresetChange = (preset) => {
        setSelectedPreset(preset);
        const newDateRange = getPresetDateRange(preset);
        setStartDate(newDateRange.startDate);
        setEndDate(newDateRange.endDate);
        setShowDatePicker(false);
    };

    // Handle custom date selection with range picker
    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        
        // If both dates are selected, update preset and close picker
        if (start && end) {
            setSelectedPreset('Aangepast'); // Set to custom when user picks dates
            setShowDatePicker(false); // Close picker after selecting end date
        }
    };

    // Fetch main analytics data (without real-time data)
    const fetchAnalyticsData = useCallback(async () => {
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        
        try {
            // Validate dates before making API call
            if (!startDate || !endDate) {
                console.error('Missing dates:', { startDate, endDate });
                setAnalyticsError('Ongeldige datums geselecteerd');
                return;
            }

            const formattedStartDate = formatDateForApi(startDate);
            const formattedEndDate = formatDateForApi(endDate);

            if (!formattedStartDate || !formattedEndDate) {
                console.error('Failed to format dates');
                setAnalyticsError('Fout bij formatteren van datums');
                return;
            }

            // Debug logging
            console.log('🚀 Fetching main analytics data with parameters:', {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                includeRealtime: false, // Don't include real-time data in main fetch
                function: 'getAnalyticsData'
            });

            const getAnalytics = httpsCallable(functions, 'getAnalyticsData');
            const result = await getAnalytics({
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                includeRealtime: false // Don't include real-time data in main fetch
            });
            
            console.log('📊 Main Analytics API response received:', {
                success: result.data?.success,
                pageStatsCount: result.data?.analytics?.pageViews?.pageStats?.length || 0,
                visitorStats: result.data?.analytics?.visitorStats
            });
            
            // Debug visitor stats to check totalUsers issue
            if (result.data?.analytics?.visitorStats) {
                console.log('👥 Visitor Stats Debug:', {
                    totalVisitors: result.data.analytics.visitorStats.totalVisitors,
                    uniqueVisitors: result.data.analytics.visitorStats.uniqueVisitors,
                    returningVisitors: result.data.analytics.visitorStats.returningVisitors,
                    activeVisitors: result.data.analytics.visitorStats.activeVisitors
                });
            }
            
            // Debug page stats
            if (result.data?.analytics?.pageViews?.pageStats) {
                console.log('📄 Page statistics received from backend:', {
                    count: result.data.analytics.pageViews.pageStats.length,
                    allPages: result.data.analytics.pageViews.pageStats.map(page => ({
                        title: page.pageTitle,
                        path: page.pagePath,
                        views: page.totalViews,
                        users: page.activeUsers,
                        avgTime: page.avgTimeOnPage
                    }))
                });
            } else {
                console.warn('❌ No page statistics found in response');
            }
            
            if (result.data && result.data.success) {
                // Preserve existing real-time data when updating main analytics
                setAnalyticsData(prevData => ({
                    ...result.data.analytics,
                    realtimeData: prevData.realtimeData // Keep existing real-time data
                }));
                setLastUpdated(new Date());
                console.log('Main analytics data loaded successfully');
            } else {
                throw new Error(result.data?.error || 'Failed to fetch analytics data');
            }
        } catch (error) {
            console.error('Error fetching main analytics:', error);
            setAnalyticsError(error.message);
            setShowAlert(true);
            setAlertMessage('Kon geen analytics data ophalen. Probeer het opnieuw.');
            setAlertType('danger');
        } finally {
            setAnalyticsLoading(false);
        }
    }, [startDate, endDate]);

    // Fetch only real-time data for active users
    const fetchRealtimeData = useCallback(async () => {
        if (!token) return;
        
        setRealtimeLoading(true);
        
        try {
            console.log('🔄 Fetching real-time data only...');

            const getAnalytics = httpsCallable(functions, 'getAnalyticsData');
            const result = await getAnalytics({
                startDate: formatDateForApi(new Date()), // Today only for real-time
                endDate: formatDateForApi(new Date()),
                includeRealtime: true // Only real-time data
            });
            
            console.log('⚡ Real-time API response received:', {
                success: result.data?.success,
                hasRealtimeData: !!result.data?.analytics?.realtimeData,
                realtimeTimeSeries: result.data?.analytics?.realtimeData?.timeSeries?.length || 0,
                currentActiveUsers: result.data?.analytics?.realtimeData?.currentActiveUsers,
                dataSource: result.data?.analytics?.realtimeData?.dataSource
            });
            
            if (result.data && result.data.success && result.data.analytics?.realtimeData) {
                // Only update real-time data, preserve all other analytics data
                setAnalyticsData(prevData => ({
                    ...prevData,
                    realtimeData: result.data.analytics.realtimeData,
                    visitorStats: {
                        ...prevData.visitorStats,
                        activeVisitors: result.data.analytics.realtimeData.currentActiveUsers || 0
                    }
                }));
                setRealtimeLastUpdated(new Date());
                console.log('Real-time data updated successfully');
            }
        } catch (error) {
            console.error('Error fetching real-time data:', error);
            // Don't show alerts for real-time data errors to avoid spam
        } finally {
            setRealtimeLoading(false);
        }
    }, [token]);

    // Load analytics data when component mounts or date range changes
    useEffect(() => {
        console.log('useEffect triggered with:', { token: !!token, startDate, endDate });
        if (token && startDate && endDate) {
            console.log('Conditions met, calling fetchAnalyticsData and fetchRealtimeData');
            fetchAnalyticsData();
            // Also fetch initial real-time data
            fetchRealtimeData();
        } else {
            console.log('Conditions not met for fetching data');
        }
    }, [token, startDate, endDate, fetchAnalyticsData, fetchRealtimeData]);

    // Close date picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDatePicker && !event.target.closest('.position-relative')) {
                setShowDatePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    // Set page title
    useEffect(() => {
        document.title = "Ukie's Admin";
    }, []);

    // // Auto refresh real-time data every minute
    // useEffect(() => {
    //     if (!token) return;
        
    //     const interval = setInterval(() => {
    //         fetchRealtimeData();
    //     }, 60 * 1000); // 1 minute for real-time data only
        
    //     return () => clearInterval(interval);
    // }, [token, fetchRealtimeData]);

    // // Auto refresh main analytics data every 10 minutes
    // useEffect(() => {
    //     if (!token || !startDate || !endDate) return;
        
    //     const interval = setInterval(() => {
    //         fetchAnalyticsData();
    //     }, 10 * 60 * 1000); // 10 minutes for main analytics
        
    //     return () => clearInterval(interval);
    // }, [token, startDate, endDate, fetchAnalyticsData]);

    // Format numbers
    const formatNumber = (num) => {
        return new Intl.NumberFormat('nl-NL').format(num);
    };

    // Format duration
    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // Calculate series statistics from page data
    const getSeriesStats = () => {
        const videoPages = analyticsData?.pageViews?.pageStats?.filter(page => 
            page.pagePath.includes('/series/video') && 
            page.pageTitle.startsWith("Ukie's Academy")
        ) || [];
        
        const seriesStats = {};
        videoPages.forEach(page => {
            // Extract series name from "Ukie's Academy - Series Name - Video Name"
            const parts = page.pageTitle.split(' - ');
            if (parts.length >= 3) {
                const seriesName = parts[1];
                if (!seriesStats[seriesName]) {
                    seriesStats[seriesName] = {
                        name: seriesName,
                        totalViews: 0,
                        activeUsers: 0,
                        totalTimeOnPage: 0,
                        videoCount: 0
                    };
                }
                seriesStats[seriesName].totalViews += page.totalViews;
                seriesStats[seriesName].activeUsers += page.activeUsers;
                // Calculate total time spent (time per page * total views)
                seriesStats[seriesName].totalTimeOnPage += (page.avgTimeOnPage * page.totalViews);
                seriesStats[seriesName].videoCount += 1;
            }
        });
        
        // Sort by total time spent (most time = most popular)
        const sortedSeries = Object.values(seriesStats).sort((a, b) => b.totalTimeOnPage - a.totalTimeOnPage);
        
        console.log('📺 Series statistics:', {
            videoPages: videoPages.length,
            seriesFound: Object.keys(seriesStats).length,
            topSeries: sortedSeries[0],
            sortedByTotalTime: sortedSeries.map(s => ({ name: s.name, totalTime: s.totalTimeOnPage, totalViews: s.totalViews }))
        });
        
        return sortedSeries;
    };

    // Prepare chart data - NO FAKE DATA
    const getChartData = () => {
        if (!analyticsData || !analyticsData.timeSeries || analyticsData.timeSeries.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        let labels = analyticsData.timeSeries.map(item => 
            moment(item.date, 'YYYYMMDD').format('DD MMM')
        );

        const getDataForMetric = (metric) => {
            return analyticsData.timeSeries.map(item => item[metric] || 0);
        };

        const datasets = [];

        if (selectedMetric === 'activeUsers') {
            // Only show real Google Analytics data - NO FAKE DATA
            console.log('🔍 Active users - DEBUG realtimeData structure:', {
                hasRealtimeData: !!analyticsData.realtimeData,
                realtimeTimeSeries: analyticsData.realtimeData?.timeSeries?.length || 0,
                currentActiveUsers: analyticsData.realtimeData?.currentActiveUsers,
                activeVisitors: analyticsData.visitorStats?.activeVisitors,
                dataSource: analyticsData.realtimeData?.dataSource || 'No real-time data',
                sampleTimeSeries: analyticsData.realtimeData?.timeSeries?.slice(0, 3)
            });
            
            // Check if we have minute-by-minute real-time data from Google Analytics
            if (analyticsData.realtimeData?.timeSeries && analyticsData.realtimeData.timeSeries.length > 0) {
                // Use minute-by-minute real-time data from GA4 Real-time API
                const realtimeLabels = analyticsData.realtimeData.timeSeries.map(item => {
                    const date = new Date(item.date || item.datetime);
                    return date.toLocaleTimeString('nl-NL', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    });
                });
                
                const realtimeData = analyticsData.realtimeData.timeSeries.map(item => {
                    const actualValue = parseInt(item.activeUsers || item.rt_activeUsers || 0);
                    // Show tiny bar (0.1) for zero values, but keep actual data for tooltips
                    return actualValue === 0 ? 0.1 : actualValue;
                });
                
                // Store actual values for tooltips
                const actualData = analyticsData.realtimeData.timeSeries.map(item => 
                    parseInt(item.activeUsers || item.rt_activeUsers || 0)
                );
                
                const currentCount = analyticsData.realtimeData.currentActiveUsers || 0;
                const totalUsers = realtimeData.reduce((sum, val) => sum + val, 0);
                const avgUsers = totalUsers > 0 ? Math.round(totalUsers / realtimeData.length) : 0;
                
                console.log(`Using REAL GA minute-by-minute data: ${realtimeData.length} minutes, current: ${currentCount}, avg: ${avgUsers}`);
                
                // Override labels - show 7 evenly distributed labels
                labels = realtimeLabels.map((label, index) => {
                    // Show 7 labels: indices 0, 5, 10, 15, 20, 25, 29
                    const showIndices = [0, 5, 10, 15, 20, 25, 29];
                    if (showIndices.includes(index)) {
                        return label;
                    }
                    return '';
                });
                
                datasets.push({
                    label: `Actieve Gebruikers per Minuut (live van GA4)`,
                    data: realtimeData,
                    backgroundColor: brandColors.primaryLight + '80',
                    borderWidth: 0,
                    actualData: actualData, // Store actual values for tooltips
                });
            } else if (analyticsData.realtimeData?.currentActiveUsers !== undefined) {
                // Fallback: show current snapshot if no minute-by-minute data
                const actualCount = analyticsData.realtimeData.currentActiveUsers;
                console.log(`Using GA current snapshot: ${actualCount} active users`);
                
                const now = new Date();
                const timeLabel = now.toLocaleTimeString('nl-NL', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                });
                
                labels = [timeLabel];
                
                datasets.push({
                    label: `Actieve Gebruikers Nu: ${actualCount} (live van GA4)`,
                    data: [actualCount === 0 ? 0.1 : actualCount], // Show tiny bar for zero
                    backgroundColor: brandColors.primaryLight + '80',
                    borderWidth: 0,
                    actualData: [actualCount], // Store actual value for tooltip
                });
            } else {
                // NO DATA AVAILABLE - Don't show fake data
                console.warn('No real-time data available from Google Analytics API');
                
                labels = ['Geen data'];
                datasets.push({
                    label: 'Geen real-time data van Google Analytics',
                    data: [0.1], // Show tiny bar instead of nothing
                    backgroundColor: '#cccccc40',
                    borderWidth: 0,
                    actualData: [0], // Store actual value for tooltip
                });
            }
        } else if (selectedMetric === 'pageViews') {
            datasets.push({
                label: 'Paginaweergaven',
                data: getDataForMetric('pageViews'),
                borderColor: brandColors.primaryLight,
                backgroundColor: brandColors.primaryLight + '20', // 20% opacity
                fill: true,
            });
        } else if (selectedMetric === 'visitorStats') {
            // Total visitors line (use activeUsers as proxy for total visitors)
            const totalVisitorsData = getDataForMetric('totalVisitors');
            const activeUsersData = getDataForMetric('activeUsers');
            datasets.push({
                label: 'Totale Bezoekers',
                data: totalVisitorsData.some(val => val > 0) ? totalVisitorsData : activeUsersData,
                borderColor: brandColors.primaryLight,
                backgroundColor: brandColors.primaryLight + '20', // 20% opacity
                fill: false,
            });
            // Unique visitors line - only use real GA data
            const uniqueVisitorsData = getDataForMetric('uniqueVisitors');
            const newUsersData = getDataForMetric('newUsers');
            
            console.log('Visitor data sources:', {
                uniqueVisitorsData: uniqueVisitorsData.slice(0, 3),
                newUsersData: newUsersData.slice(0, 3),
                usingUniqueVisitors: uniqueVisitorsData.some(val => val > 0),
                usingNewUsers: newUsersData.some(val => val > 0)
            });
            
            // Use real data only - no fake fallbacks
            let finalUniqueData;
            if (uniqueVisitorsData.some(val => val > 0)) {
                finalUniqueData = uniqueVisitorsData;
                console.log('Using uniqueVisitors data from GA');
            } else if (newUsersData.some(val => val > 0)) {
                finalUniqueData = newUsersData;
                console.log('Using newUsers data from GA');
            } else {
                finalUniqueData = uniqueVisitorsData; // Show zeros if no real data
                console.warn('No unique visitor data available from GA - showing zeros');
            }
            
            datasets.push({
                label: 'Nieuwe Bezoekers',
                data: finalUniqueData,
                borderColor: brandColors.secondaryDark,
                backgroundColor: brandColors.secondaryDark + '20', // 20% opacity
                fill: false,
            });
        }

        return { labels, datasets };
    };

    // Prepare series chart data
    const getSeriesChartData = () => {
        const allSeries = getSeriesStats();
        
        if (!allSeries || allSeries.length === 0) {
            return {
                labels: ['Geen data'],
                datasets: [{
                    label: 'Totale kijktijd (seconden)',
                    data: [0],
                    backgroundColor: brandColors.primary,
                    borderColor: brandColors.primaryDark,
                    borderWidth: 1
                }]
            };
        }

        // Take top 10 series or all if less than 10
        const topSeries = allSeries.slice(0, 10);
        
        return {
            labels: topSeries.map(series => series.name),
            datasets: [{
                label: 'Totale kijktijd (seconden)',
                data: topSeries.map(series => Math.round(series.totalTimeOnPage)),
                backgroundColor: topSeries.map((_, index) => 
                    index === 0 ? brandColors.primaryDark : 
                    index === 1 ? brandColors.primary : 
                    index === 2 ? brandColors.primaryLight : 
                    brandColors.secondary
                ),
                borderColor: brandColors.primaryDark,
                borderWidth: 1
            }]
        };
    };

    // Series chart options
    const getSeriesChartOptions = () => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            const totalTime = context.parsed.y;
                            return `Totale kijktijd: ${formatDuration(totalTime)}`;
                        }
                    }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: function(value) {
                            return formatDuration(value);
                        }
                    },
                    title: {
                        display: true,
                        text: 'Totale kijktijd'
                    }
                },
                x: {
                    ticks: {
                        maxRotation: 45,
                        minRotation: 0
                    },
                    title: {
                        display: true,
                        text: 'Series'
                    }
                }
            }
        };
    };

    // Get percentage change
    const getPercentageChange = (current, previous) => {
        if (previous === 0) return current > 0 ? '+100%' : '0%';
        const change = ((current - previous) / previous) * 100;
        return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
    };

    onAuthStateChanged(auth, (user) => {
        if (user) {
        // User is signed in > Refresh token
        } else {
        // User is signed out > Remove local storage
        localStorage.removeItem('ukies-webinars-admin')
        localStorage.removeItem('ukies-webinars-accessToken')
        setToken()
        setUser()
        }
    })

    if(!token) {
        return <Stack><Login user={user} setUser={setUser} token={token} setToken={setToken} /></Stack>
    }

    return (
        <>
            <Navigation user={user} currentPage='home'/>
            
            {showAlert && (
                <Alert 
                variant={alertType} 
                dismissible 
                onClose={() => setShowAlert(false)} 
                style={{
                    position: 'fixed',
                    top: '15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1060,
                    minWidth: '300px',
                    maxWidth: '600px'
                }}
            >
                    {alertMessage}
                </Alert>
            )}
            
            <Stack style={{marginTop:'50px',padding:'1rem 0'}}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 style={{fontSize:'2.5rem', marginBottom: '0.5rem'}}>Analytics Dashboard</h1>
                        {(lastUpdated || realtimeLastUpdated) && (
                            <div>
                                {lastUpdated && (
                                    <small className="text-muted d-block">
                                        Dashboard bijgewerkt: {moment(lastUpdated).format('DD-MM-YYYY HH:mm')}
                                    </small>
                                )}
                                {/* {realtimeLastUpdated && (
                                    <small className="text-muted d-block">
                                        Live data bijgewerkt: {moment(realtimeLastUpdated).format('HH:mm:ss')}
                                        {realtimeLoading && <span className="ms-1">(wordt bijgewerkt...)</span>}
                                    </small>
                                )} */}
                            </div>
                        )}
                    </div>
                    <div className="d-flex gap-2 align-items-start">
                        {/* Date Range Group */}
                        <div className="d-flex flex-column">
                            <div className="d-flex gap-2 align-items-center">
                                {/* Date Range Display and Trigger */}
                                <div className="position-relative">
                                    <button 
                                        className="btn d-flex align-items-center gap-2" 
                                        onClick={() => setShowDatePicker(!showDatePicker)}
                                        style={{
                                            minWidth: '200px',
                                            backgroundColor: 'white',
                                            border: `1px solid ${brandColors.primaryDark}`,
                                            color: brandColors.primaryDark
                                        }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                                        </svg>
                                        <span className="text-start" style={{flex: 1}}>
                                            {selectedPreset === 'Aangepast' 
                                                ? `${moment(startDate).format('DD-MM-YYYY')} - ${moment(endDate).format('DD-MM-YYYY')}`
                                                : selectedPreset
                                            }
                                        </span>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                                        </svg>
                                    </button>
                            
                            {/* Date Range Picker Dropdown */}
                            {showDatePicker && (
                                <div 
                                    className="position-absolute bg-white border rounded shadow-lg p-3" 
                                    style={{
                                        top: '100%', 
                                        left: 0, 
                                        zIndex: 1050, 
                                        minWidth: '300px',
                                        marginTop: '5px'
                                    }}
                                >
                                    {/* Preset Options */}
                                    <div className="mb-3">
                                        <h6 className="mb-2">Snelle selectie</h6>
                                        <div className="d-grid gap-1">
                                            {[
                                                'Vandaag',
                                                'Dit jaar',
                                                'Deze maand', 
                                                'Deze week',
                                                'Laatste 30 dagen',
                                                'Laatste 90 dagen'
                                            ].map(preset => (
                                                <button
                                                    key={preset}
                                                    className="btn btn-sm text-start"
                                                    style={{
                                                        backgroundColor: selectedPreset === preset ? brandColors.primaryDark : 'transparent',
                                                        color: selectedPreset === preset ? 'white' : brandColors.primaryDark,
                                                        border: `1px solid ${brandColors.primaryDark}`,
                                                        marginBottom: '2px'
                                                    }}
                                                    onClick={() => handlePresetChange(preset)}
                                                >
                                                    {preset}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Custom Date Range Picker */}
                                    <div className="border-top pt-3">
                                        <h6 className="mb-2">Aangepaste periode</h6>
                                        <div className="mb-3">
                                            <label className="form-label small">
                                                Selecteer datumbereik:
                                            </label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={handleDateChange}
                                                startDate={startDate}
                                                endDate={endDate}
                                                selectsRange={true}
                                                maxDate={new Date()}
                                                dateFormat="dd/MM/yyyy"
                                                className="form-control"
                                                placeholderText="Selecteer datumbereik"
                                                locale="nl"
                                                inline
                                            />
                                        </div>

                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="d-flex justify-content-between mt-3">
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: brandColors.disabled,
                                                border: `1px solid ${brandColors.disabled}`,
                                            }}
                                            onClick={() => {
                                                setStartDate(moment().subtract(30, 'days').toDate());
                                                setEndDate(new Date());
                                                setSelectedPreset('Laatste 30 dagen');
                                            }}
                                        >
                                            Reset
                                        </button>
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: brandColors.primaryDark,
                                                color: 'white',
                                                border: `1px solid ${brandColors.primaryDark}`,
                                            }}
                                            onClick={() => setShowDatePicker(false)}
                                        >
                                            Sluiten
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                                                        <button 
                                    className="btn d-flex align-items-center justify-content-center" 
                                    onClick={() => {
                                        fetchAnalyticsData();
                                        fetchRealtimeData();
                                    }}
                                    disabled={analyticsLoading}
                                    title="Vernieuw alle dashboard data"
                                    style={{
                                        backgroundColor: brandColors.primaryDark,
                                        color: 'white',
                                        border: `1px solid ${brandColors.primaryDark}`,
                                        width: '37.6px',
                                        height: '37.6px',
                                        padding: 0
                                    }}
                                >
                                    {analyticsLoading ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"></path>
                                            <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"></path>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <small className="text-muted mt-1" style={{fontSize: '0.75rem'}}>
                                {moment(startDate).format('DD-MM-YYYY')} - {moment(endDate).format('DD-MM-YYYY')}
                            </small>
                        </div>
                    </div>
                </div>

                {analyticsLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Analytics data laden...</p>
                    </div>
                ) : (
                    <>
                        {/* Key Metrics Cards */}
                <Row className="mb-4">
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="h-100" style={{
                                    cursor: 'pointer',
                                    border: selectedMetric === 'visitorStats' ? `2px solid ${brandColors.secondaryDark}` : '1px solid #dee2e6',
                                    boxShadow: selectedMetric === 'visitorStats' ? `0 0 0 3px ${brandColors.secondaryDark}20` : ''
                                }}
                                      onClick={() => setSelectedMetric('visitorStats')}>
                                    <Card.Body style={{position: 'relative', overflow: 'hidden'}}>
                                        <div className="d-flex justify-content-between align-items-end" style={{position: 'relative', zIndex: 1}}>
                                            <div>
                                                <h6 className="text-muted mb-1">Bezoekers</h6>
                                                <div className="d-flex align-items-end gap-4">
                                                    <div className="d-flex align-items-end gap-1">
                                                        <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                            {analyticsData && analyticsData.visitorStats ? 
                                                                formatNumber(analyticsData.visitorStats.totalVisitors) : 
                                                                'Geen data'
                                                            }
                                                        </h3>
                                                        <small style={{color: 'var(--ukies-inactive)', opacity: '0.8', fontStyle: 'italic', paddingBottom: '0.5rem'}}>Totaal</small>
                                                    </div>
                                                    <div className="d-flex align-items-end gap-1">
                                                        <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                            {analyticsData && analyticsData.visitorStats ? 
                                                                formatNumber(analyticsData.visitorStats.uniqueVisitors) : 
                                                                'Geen data'
                                                            }
                                                        </h3>
                                                        <small style={{color: 'var(--ukies-inactive)', opacity: '0.8', fontStyle: 'italic', paddingBottom: '0.5rem'}}>Nieuw</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="position-absolute" style={{height: '160px', opacity: 0.1, zIndex: 0, bottom: 'calc(-1rem - 13px)', right: 'calc(-1rem - 30px)'}}>
                                            <svg width="auto" height="160px" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                            </svg>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="h-100" style={{
                                    cursor: 'pointer',
                                    border: selectedMetric === 'pageViews' ? `2px solid ${brandColors.secondaryDark}` : '1px solid #dee2e6',
                                    boxShadow: selectedMetric === 'pageViews' ? `0 0 0 3px ${brandColors.secondaryDark}20` : ''
                                }}
                                      onClick={() => setSelectedMetric('pageViews')}>
                                    <Card.Body style={{position: 'relative', overflow: 'hidden'}}>
                                        <div className="d-flex justify-content-between align-items-end" style={{position: 'relative', zIndex: 1}}>
                                            <div>
                                                <h6 className="text-muted mb-1">Paginaweergaven</h6>
                                                <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                    {analyticsData && analyticsData.pageViews ? 
                                                        formatNumber(analyticsData.pageViews.totalPageViews) : 
                                                        'Geen data'
                                                    }
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="position-absolute" style={{height: '160px', opacity: 0.1, zIndex: 0, bottom: 'calc(-1rem - 13px)', right: 'calc(-1rem - 30px)'}}>
                                            <svg width="auto" height="160px" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                            </svg>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="h-100" style={{
                                    cursor: 'pointer',
                                    border: selectedMetric === 'activeUsers' ? `2px solid ${brandColors.secondaryDark}` : '1px solid #dee2e6',
                                    boxShadow: selectedMetric === 'activeUsers' ? `0 0 0 3px ${brandColors.secondaryDark}20` : ''
                                }} 
                                      onClick={() => setSelectedMetric('activeUsers')}>
                                    <Card.Body style={{position: 'relative', overflow: 'hidden'}}>
                                        <div className="d-flex justify-content-between align-items-center" style={{position: 'relative', zIndex: 1}}>
                                            <div>
                                                <h6 className="text-muted mb-1">Actieve Gebruikers</h6>
                                                <div className="d-flex align-items-end gap-1">
                                                    <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                        {realtimeLoading ? (
                                                            <Spinner size="sm" className="me-2" />
                                                        ) : (
                                                            analyticsData && analyticsData.visitorStats?.activeVisitors !== undefined 
                                                                ? formatNumber(analyticsData.visitorStats.activeVisitors)
                                                                : 'Geen data'
                                                        )}
                                                    </h3>
                                                    <small style={{color: 'var(--ukies-inactive)', opacity: '0.8', fontStyle: 'italic', paddingBottom: '0.5rem'}}>
                                                        {realtimeLoading ? 'Bijwerken...' : 
                                                         (analyticsData && analyticsData.visitorStats?.activeVisitors !== undefined ? 'Op dit moment' : 'Geen data')}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="position-absolute" style={{height: '160px', opacity: 0.1, zIndex: 0, bottom: 'calc(-1rem - 13px)', right: 'calc(-1rem - 30px)'}}>
                                            <svg width="auto" height="160px" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                            </svg>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={3} md={6} className="mb-3">
                                <Card className="h-100" style={{
                                    cursor: 'pointer',
                                    border: selectedMetric === 'popularSeries' ? `2px solid ${brandColors.secondaryDark}` : '1px solid #dee2e6',
                                    boxShadow: selectedMetric === 'popularSeries' ? `0 0 0 3px ${brandColors.secondaryDark}20` : ''
                                }}
                                      onClick={() => setSelectedMetric('popularSeries')}>
                                    <Card.Body style={{position: 'relative', overflow: 'hidden'}}>
                                        <div className="d-flex justify-content-between align-items-end" style={{position: 'relative', zIndex: 1}}>
                                            <div>
                                                {(() => {
                                                    const allSeries = getSeriesStats();
                                                    const topSeries = allSeries[0];
                                                    
                                                    if (!topSeries) {
                                                        return (
                                                            <div>
                                                                <h6 className="text-muted mb-1">Populairste Serie</h6>
                                                                <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                                    Geen data
                                                                </h3>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    return (
                                                        <div>
                                                            <h6 className="text-muted mb-1">{topSeries.name}</h6>
                                                            <div className="d-flex align-items-end gap-1">
                                                                <h3 className="mb-0 p-0" style={{color: brandColors.secondaryDark}}>
                                                                    {formatDuration(Math.round(topSeries.totalTimeOnPage))}
                                                                </h3>
                                                                <small style={{color: 'var(--ukies-inactive)', opacity: '0.8', fontStyle: 'italic', paddingBottom: '0.5rem'}}>Totale kijktijd</small>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="position-absolute" style={{height: '160px', opacity: 0.1, zIndex: 0, bottom: 'calc(-1rem - 13px)', right: 'calc(-1rem - 30px)'}}>
                                            <svg width="auto" height="160px" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M8 5v14l11-7z"/>
                                            </svg>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Time Series Chart */}
                        <Card className="mb-4">
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        {selectedMetric === 'visitorStats' && 'Bezoekers'}
                                        {selectedMetric === 'pageViews' && 'Paginaweergaven'}
                                        {selectedMetric === 'activeUsers' && (
                                            <span>
                                                Actieve Gebruikers per Minuut (laatste 30 min)
                                                {realtimeLoading && <Spinner size="sm" className="ms-2" />}
                                            </span>
                                        )}
                                        {selectedMetric === 'popularSeries' && 'Populaire Series (op basis van totale kijktijd)'}
                                    </h5>
                                    <div className="btn-group btn-group-sm">
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: selectedMetric === 'visitorStats' ? brandColors.primaryLight : 'transparent',
                                                color: selectedMetric === 'visitorStats' ? 'white' : brandColors.primaryLight,
                                                border: `1px solid ${brandColors.primaryLight}`,
                                            }}
                                            onClick={() => setSelectedMetric('visitorStats')}
                                        >
                                            Bezoekers
                                        </button>
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: selectedMetric === 'pageViews' ? brandColors.primaryLight : 'transparent',
                                                color: selectedMetric === 'pageViews' ? 'white' : brandColors.primaryLight,
                                                border: `1px solid ${brandColors.primaryLight}`,
                                            }}
                                            onClick={() => setSelectedMetric('pageViews')}
                                        >
                                            Weergaven
                                        </button>
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: selectedMetric === 'activeUsers' ? brandColors.primaryLight : 'transparent',
                                                color: selectedMetric === 'activeUsers' ? 'white' : brandColors.primaryLight,
                                                border: `1px solid ${brandColors.primaryLight}`,
                                            }}
                                            onClick={() => setSelectedMetric('activeUsers')}
                                        >
                                            Gebruikers
                                        </button>
                                        <button 
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: selectedMetric === 'popularSeries' ? brandColors.primaryLight : 'transparent',
                                                color: selectedMetric === 'popularSeries' ? 'white' : brandColors.primaryLight,
                                                border: `1px solid ${brandColors.primaryLight}`,
                                            }}
                                            onClick={() => setSelectedMetric('popularSeries')}
                                        >
                                            Series
                                        </button>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div style={{ height: '400px' }}>
                                    {(selectedMetric === 'activeUsers' && !analyticsData.realtimeData?.timeSeries?.length && analyticsData.realtimeData?.currentActiveUsers === undefined) ? (
                                        <div className="d-flex align-items-center justify-content-center h-100 text-center">
                                            <div>
                                                <p className="text-muted mb-2">Geen real-time data van Google Analytics</p>
                                                <small className="text-muted">
                                                    Real-time data wordt opgehaald van GA4 Real-time API.<br />
                                                    {analyticsData.visitorStats?.activeVisitors !== undefined 
                                                        ? `Huidige actieve gebruikers: ${analyticsData.visitorStats.activeVisitors}`
                                                        : 'Controleer de backend configuratie.'
                                                    }
                                                </small>
                                            </div>
                                        </div>
                                    ) : (analyticsData.timeSeries && analyticsData.timeSeries.length > 0) || (selectedMetric === 'activeUsers') || (selectedMetric === 'popularSeries' && getSeriesStats().length > 0) ? (
                                        selectedMetric === 'activeUsers' ? (
                                            <Bar data={getChartData()} options={chartOptions} />
                                        ) : selectedMetric === 'popularSeries' ? (
                                            <Bar data={getSeriesChartData()} options={getSeriesChartOptions()} />
                                        ) : (
                                            <Line data={getChartData()} options={chartOptions} />
                                        )
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center h-100">
                                            {selectedMetric === 'popularSeries' ? (
                                                <div className="text-center">
                                                    <p className="text-muted mb-2">Geen video series data beschikbaar</p>
                                                    <small className="text-muted">
                                                        Series data wordt berekend op basis van /series/video pagina's.<br />
                                                        Controleer of er video pagina's zijn bezocht in de geselecteerde periode.
                                                    </small>
                                                </div>
                                            ) : (
                                                <p className="text-muted">Geen data beschikbaar voor de geselecteerde periode</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Detailed Stats */}
                        <Row className="mb-4">
                            <Col lg={6}>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Paginastatistieken</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        {(() => {
                                            // Filter pages in frontend - only include Ukie's Academy pages
                                            const filteredPages = analyticsData?.pageViews?.pageStats?.filter(page => {
                                                return page.pageTitle.startsWith("Ukie's Academy");
                                            }) || [];
                                            
                                            console.log("Frontend filtering results:", {
                                                totalPages: analyticsData?.pageViews?.pageStats?.length || 0,
                                                filteredPages: filteredPages.length,
                                                academyPages: analyticsData?.pageViews?.pageStats?.filter(p => p.pageTitle.startsWith("Ukie's Academy")).length,
                                                filteredTitles: filteredPages.map(p => p.pageTitle).slice(0, 5)
                                            });
                                            
                                            return filteredPages.length > 0;
                                        })() ? (
                                            <Table hover>
                                                <thead>
                                                    <tr>
                                                        <th>Pagina Titel</th>
                                                        <th className="text-end">Weergaven</th>
                                                        <th className="text-end">Actieve Gebruikers</th>
                                                        <th className="text-end">Gem. Tijd</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        // Calculate filtered pages for table body
                                                        const filteredPages = analyticsData?.pageViews?.pageStats?.filter(page => {
                                                            return page.pageTitle.startsWith("Ukie's Academy");
                                                        }) || [];
                                                        
                                                        return filteredPages.map((page, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-2" style={{minWidth: '16px', minHeight: '16px', flexShrink: 0}}>
                                                                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" fill="currentColor"/>
                                                                    </svg>
                                                                    <div>
                                                                        <div>{page.pageTitle}</div>
                                                                        <small className="text-muted" style={{fontSize: '0.75rem', lineHeight: 1.2}}>
                                                                            {page.pagePath || 'No path available'}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-end">{formatNumber(page.totalViews)}</td>
                                                            <td className="text-end" style={{color: brandColors.primaryLight}}>
                                                                {formatNumber(page.activeUsers)}
                                                            </td>
                                                            <td className="text-end">
                                                                {page.avgTimeOnPage > 0 ? formatDuration(page.avgTimeOnPage) : 'N/A'}
                                                            </td>
                                                        </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </Table>
                                        ) : (
                                            <div className="text-center py-4">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3" style={{opacity: 0.3}}>
                                                    <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" fill="currentColor"/>
                                                </svg>
                                                <h6 className="text-muted">Geen paginastatistieken beschikbaar</h6>
                                                <p className="text-muted small mb-0">Geen GA4 data beschikbaar voor de geselecteerde periode</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                                                        </Col>
                            <Col lg={6}>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Gebruikers per Locatie</h5>
                                    </Card.Header>
                                    <Card.Body style={{ padding: '0.5rem' }}>
                        {(() => {
                            console.log('🏛️ UkiesAdmin passing location data:', {
                                hasCityData: !!analyticsData?.cityLocationData,
                                hasRegionData: !!analyticsData?.regionLocationData,
                                cityCount: analyticsData?.cityLocationData?.length || 0,
                                regionCount: analyticsData?.regionLocationData?.length || 0,
                                sampleCityData: analyticsData?.cityLocationData?.slice(0, 2),
                                sampleRegionData: analyticsData?.regionLocationData?.slice(0, 2)
                            });
                            return null;
                        })()}
                        <UserLocationMap 
                            cityLocationData={analyticsData?.cityLocationData || []}
                            regionLocationData={analyticsData?.regionLocationData || []}
                        />
                    </Card.Body>
                                </Card>
                            </Col>
                </Row>
                    </>
                )}

                {analyticsError && (
                    <div className="alert alert-danger">
                        <strong>Fout:</strong> {analyticsError}
                    </div>
                )}
            </Stack>
        </>
    );
}